import React, { useState, useContext, useMemo, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import SignaturePadModal from './SignaturePadModal';
import { 
  X, 
  FileText, 
  ShieldCheck, 
  UserCheck, 
  Calendar, 
  Check, 
  PenTool, 
  RotateCcw,
  Sparkles, 
  AlertCircle,
  HelpCircle,
  Building2,
  CheckCircle2,
  Maximize2,
  Stamp,
  Search,
  ChevronDown,
  Paperclip,
  Upload,
  Trash2,
  File,
  FileSpreadsheet
} from 'lucide-react';

const RESIGNATION_REASONS = [
  '일신상의 사유 (개인 사정)',
  '타사 이직 및 새로운 커리어 도전',
  '학업 및 진학 / 어학 연수',
  '건강상의 이유 및 요양',
  '가사 / 육아 / 출산 사정',
  '근로계약 기간 만료',
  '직접 입력'
];

const ResignationSubmitModal = ({ isOpen, onClose, onSubmittedSuccess }) => {
  const { currentUser, users, contracts, resignationPolicy, createContract } = useContext(AppContext);

  if (!isOpen) return null;

  const fileInputRef = useRef(null);

  const [resignationDate, setResignationDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [reasonCategory, setReasonCategory] = useState('일신상의 사유 (개인 사정)');
  const [customReason, setCustomReason] = useState('');
  const [handoverRecipientId, setHandoverRecipientId] = useState('');
  const [handoverSearchTerm, setHandoverSearchTerm] = useState('');
  const [isHandoverDropdownOpen, setIsHandoverDropdownOpen] = useState(false);
  const [handoverDetails, setHandoverDetails] = useState('1. 담당 업무 소스코드 및 문서 인계\n2. AWS/클라우드 및 협업툴 계정 권한 이관\n3. PC 및 사원증 반납');
  
  // Handover File Attachments (Optional)
  const [attachedFiles, setAttachedFiles] = useState([]); // [{ name, size, type, dataUrl }]

  // Checklist confirmations
  const [confirmHandover, setConfirmHandover] = useState(true);
  const [confirmAssetReturn, setConfirmAssetReturn] = useState(true);
  const [confirmSecurityNda, setConfirmSecurityNda] = useState(true);

  // Digital Signature & SignaturePadModal
  const [isSignaturePadOpen, setIsSignaturePadOpen] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null); // { dataUrl, type }
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Active employees eligible for handover (excluding retired, resigned, inactive, and current user)
  const eligibleHandoverUsers = useMemo(() => {
    return (users || []).filter(u => {
      if (u.UserID === currentUser?.UserID) return false;
      const status = (u.Status || u.status || '').toUpperCase();
      if (status === 'RETIRED' || status === 'RESIGNED' || status === 'INACTIVE' || u.IsRetired === true || u.Resigned === true) {
        return false;
      }
      const isApprovedResigned = contracts?.some(c => 
        (c.UserID === u.UserID || c.UserName === u.Name) && 
        c.IsResignation && 
        (c.Status === 'APPROVED' || c.ResignationStatus === 'APPROVED')
      );
      if (isApprovedResigned) return false;
      return true;
    });
  }, [users, currentUser, contracts]);

  // Search filtered list
  const filteredHandoverUsers = useMemo(() => {
    if (!handoverSearchTerm.trim()) return eligibleHandoverUsers;
    const term = handoverSearchTerm.toLowerCase();
    return eligibleHandoverUsers.filter(u => 
      (u.Name && u.Name.toLowerCase().includes(term)) ||
      (u.Department && u.Department.toLowerCase().includes(term)) ||
      (u.Position && u.Position.toLowerCase().includes(term)) ||
      (u.JobTitle && u.JobTitle.toLowerCase().includes(term)) ||
      (u.Email && u.Email.toLowerCase().includes(term))
    );
  }, [eligibleHandoverUsers, handoverSearchTerm]);

  const handoverUser = eligibleHandoverUsers.find(u => u.UserID === handoverRecipientId);

  const handleSignatureConfirmed = (dataUrl, type) => {
    setSavedSignature({ dataUrl, type });
  };

  // Handle File Upload for Handover Documents
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`파일 크기가 5MB를 초과하여 첨부할 수 없습니다: ${file.name}`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (loadEvt) => {
        setAttachedFiles(prev => [
          ...prev,
          {
            id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: file.name,
            size: file.size,
            type: file.type || 'application/octet-stream',
            dataUrl: loadEvt.target.result
          }
        ]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleRemoveFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!savedSignature?.dataUrl) {
      alert('사직원 제출을 위해 [고해상도 전자서명/도장 날인] 버튼을 눌러 서명을 완료해 주세요.');
      setIsSignaturePadOpen(true);
      return;
    }

    if (!confirmSecurityNda || !confirmAssetReturn || !confirmHandover) {
      alert('필수 준수 및 확약 사항 3가지에 모두 동의하셔야 사직원 제출이 가능합니다.');
      return;
    }

    try {
      setIsSubmitting(true);
      const signatureDataUrl = savedSignature.dataUrl;
      const finalReason = reasonCategory === '직접 입력' ? customReason : `${reasonCategory}${customReason ? ` (${customReason})` : ''}`;

      // Default or Admin Policy Custom Clauses
      const baseClauses = Array.isArray(resignationPolicy?.clauses) && resignationPolicy.clauses.length > 0
        ? resignationPolicy.clauses.map(c => ({
            title: c.title,
            content: c.content
              .replace(/\{reason\}/g, finalReason)
              .replace(/\{date\}/g, resignationDate)
              .replace(/\{handover\}/g, handoverUser?.Name || '부서장')
          }))
        : [
            { 
              title: '사직의사 통보 및 희망 퇴사일', 
              content: `본인은 ${finalReason}으로 인하여 ${resignationDate} 일자로 사직하고자 하오니 재가하여 주시기 바랍니다.` 
            },
            { 
              title: '성실한 업무 및 산출물 인수인계 확약', 
              content: `퇴사일 전까지 담당 업무 및 프로젝트 산출물, 소스코드, 사내 협업툴 계정 일체를 후임자(${handoverUser?.Name || '부서장'})에게 성실히 인수인계 완료할 것을 확약합니다.${handoverDetails ? `\n[인수인계 내역: ${handoverDetails.replace(/\n/g, ', ')}]` : ''}` 
            },
            { 
              title: '퇴사자 정보보호 및 영업비밀·지식재산 유지 서약 (NDA)', 
              content: '재직 중 지득한 회사의 기술상·경영상의 영업비밀, 고객 데이터, 소스코드 및 지식재산권을 퇴사 후에도 일체 외부에 누설하거나 부당하게 사용하지 않으며 관련 법령에 따른 민·형사상 법적 책임을 준수합니다.' 
            },
            { 
              title: '사내 비품, 장비 및 마스터 계정 반납', 
              content: '사원증, 법인카드, 업무용 PC/노트북 장비 및 클라우드 인프라/SaaS 마스터 접근 권한을 퇴사 즉시 원상태로 반납합니다.' 
            },
            { 
              title: '금품 청산 및 4대보험 자격상실', 
              content: '퇴직금 및 잔여 연차정산 수당 등 금품청산은 근로기준법에 따라 퇴사일로부터 14일 이내 지급되며 4대보험 자격상실을 확인합니다.' 
            }
          ];

      const docTitle = `${currentUser?.Name} 사원 표준 사직서 (퇴직원)`;

      const contractPayload = {
        TargetType: 'EMPLOYEE',
        UserID: currentUser?.UserID,
        UserName: currentUser?.Name,
        UserEmail: currentUser?.Email,
        UserPhone: currentUser?.Phone || '010-1234-5678',
        Department: currentUser?.Department,
        Position: currentUser?.Position || currentUser?.JobTitle,
        ContractType: 'RESIGNATION',
        Title: docTitle,
        ContractStartDate: new Date().toISOString().slice(0, 10),
        ContractEndDate: resignationDate,
        ResignationDate: resignationDate,
        ResignationReason: finalReason,
        HandoverRecipient: handoverUser ? `${handoverUser.Name} (${handoverUser.Department} ${handoverUser.Position || ''})` : '부서장 직접 이관',
        HandoverRecipientID: handoverRecipientId || null,
        HandoverRecipientName: handoverUser?.Name || '부서장',
        HandoverDetails: handoverDetails,
        AttachedFiles: attachedFiles, // Attached handover documents
        AnnualSalary: 0,
        TotalAmount: 0,
        WorkingHours: '소정근로 종료',
        WorkingPlace: '사내 인수인계 완료',
        JobDescription: `퇴직 사유: ${finalReason} / 인수인계 대상: ${handoverUser?.Name || '부서장'}`,
        CustomClauses: baseClauses,
        Status: 'PENDING',
        SignatureType: savedSignature?.type === 'DRAW' ? 'CANVAS' : 'AUTO_STAMP',
        SignatureDataUrl: signatureDataUrl,
        EmployeeSignatureData: signatureDataUrl,
        SignedAt: new Date().toISOString(),
        EmployeeSignedAt: new Date().toISOString(),
        SignedIP: '127.0.0.1 (Self-Service Secure Portal)',
        IsResignation: true
      };

      const result = await createContract(contractPayload);
      if (result) {
        alert('표준 사직원(퇴사 양식)이 정상적으로 작성 및 전자서명되어 제출되었습니다.\n전자결재선에 따라 1단계(인수인계 확인자)에게 결재 알림 메일이 발송되었습니다.');
        if (onSubmittedSuccess) onSubmittedSuccess(result);
        onClose();
      }
    } catch (err) {
      alert('사직서 제출 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-2 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-rose-600/90 flex items-center justify-center text-white shadow-md">
              <FileText className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h2 className="text-base md:text-lg font-bold">사직원 (퇴직원) 작성 및 전자서명 제출</h2>
                <span className="text-[10px] bg-rose-500/30 text-rose-200 border border-rose-400/40 px-2 py-0.5 rounded-full font-bold">
                  통합 표준 양식
                </span>
              </div>
              <p className="text-xs text-slate-300 mt-0.5">정식 사직원, 퇴사자 보안서약서(NDA) 및 인수인계 파일이 포함된 종합 문서입니다.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 md:p-6 space-y-5 text-left text-xs text-slate-800">
          
          {/* Applicant Info Summary Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-[10px] font-bold text-slate-400 block">신청 사원</span>
              <span className="font-extrabold text-slate-900">{currentUser?.Name || '알 수 없음'}</span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 block">소속 부서</span>
              <span className="font-semibold text-slate-700">{currentUser?.Department || '-'}</span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 block">직급</span>
              <span className="font-semibold text-slate-700">{currentUser?.Position || currentUser?.JobTitle || '-'}</span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 block">이메일</span>
              <span className="font-mono text-slate-600">{currentUser?.Email || '-'}</span>
            </div>
          </div>

          {/* Section 1: Resignation Dates & Handover Recipient */}
          <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 space-y-4">
            <span className="text-xs font-bold text-slate-900 block border-b border-slate-200 pb-2 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-indigo-600" />
              1. 퇴사 희망일 및 업무 인수인계 대상자
            </span>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">퇴사 희망일 (사직일자) *</label>
                <input
                  type="date"
                  required
                  value={resignationDate}
                  onChange={e => setResignationDate(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
                />
              </div>

              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-bold text-slate-700">인수인계 대상자 (후임자) *</label>
                  <span className="text-[10px] text-slate-400">재직자 검색</span>
                </div>

                {handoverUser ? (
                  <div className="p-2 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-xs">
                        {handoverUser.Name?.slice(0, 1)}
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 text-xs">
                          {handoverUser.Name} <span className="text-slate-500 font-medium text-[11px]">({handoverUser.Position || handoverUser.JobTitle || '담당'})</span>
                        </div>
                        <div className="text-[10px] text-indigo-700 font-medium">
                          {handoverUser.Department} • {handoverUser.Email || '-'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setHandoverRecipientId('');
                        setHandoverSearchTerm('');
                      }}
                      className="p-1 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-white/80 cursor-pointer"
                      title="후임자 변경"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={handoverSearchTerm}
                        onChange={e => {
                          setHandoverSearchTerm(e.target.value);
                          setIsHandoverDropdownOpen(true);
                        }}
                        onFocus={() => setIsHandoverDropdownOpen(true)}
                        placeholder="후임자 성명, 부서명, 직급 검색..."
                        className="w-full pl-9 pr-8 py-2 bg-white border border-slate-300 rounded-xl font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:border-indigo-600 text-xs"
                      />
                      {handoverSearchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setHandoverSearchTerm('');
                            setIsHandoverDropdownOpen(false);
                          }}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {/* Dropdown list */}
                    {isHandoverDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 max-h-48 bg-white border border-slate-200 rounded-xl shadow-xl overflow-y-auto z-50 divide-y divide-slate-100 animate-in fade-in zoom-in-95 duration-100">
                        <button
                          type="button"
                          onClick={() => {
                            setHandoverRecipientId('');
                            setIsHandoverDropdownOpen(false);
                          }}
                          className="w-full p-2.5 text-left hover:bg-slate-50 text-slate-600 flex items-center justify-between"
                        >
                          <span className="font-semibold text-xs">미지정 (부서장에게 직접 이관)</span>
                          <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">기본</span>
                        </button>
                        {filteredHandoverUsers.map(u => (
                          <button
                            key={u.UserID}
                            type="button"
                            onClick={() => {
                              setHandoverRecipientId(u.UserID);
                              setIsHandoverDropdownOpen(false);
                            }}
                            className="w-full p-2.5 text-left hover:bg-indigo-50/80 flex items-center justify-between transition-colors"
                          >
                            <div>
                              <span className="font-bold text-slate-900 text-xs">{u.Name}</span>
                              <span className="text-slate-500 text-[11px] ml-1.5 font-medium">({u.Position || u.JobTitle || '사원'})</span>
                              <div className="text-[10px] text-slate-400">{u.Department} • {u.Email}</div>
                            </div>
                            <span className="text-[10px] text-indigo-600 font-bold bg-indigo-50 px-2 py-0.5 rounded">선택</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Resignation Reason Selection */}
            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">퇴직 사유 선택 *</label>
              <select
                value={reasonCategory}
                onChange={e => setReasonCategory(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-indigo-600"
              >
                {RESIGNATION_REASONS.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                퇴직 사유 상세 기술 {reasonCategory === '직접 입력' && <span className="text-rose-500">*</span>}
              </label>
              <textarea
                rows={2}
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                placeholder="상세한 퇴직 사유나 회사에 전하고 싶은 말씀을 간략히 작성해 주세요..."
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-indigo-600 resize-none text-xs"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">업무 및 자산 인수인계 개요</label>
              <textarea
                rows={2}
                value={handoverDetails}
                onChange={e => setHandoverDetails(e.target.value)}
                placeholder="인수인계 진행 내역을 작성해 주세요..."
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl font-medium text-slate-800 focus:outline-none focus:border-indigo-600 resize-none text-xs"
              />
            </div>
          </div>

          {/* Section 2: Handover File Attachments (Optional) */}
          <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
              <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <Paperclip className="w-4 h-4 text-indigo-600" />
                2. 인수인계 문서 및 산출물 파일 첨부 (선택사항)
              </span>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 cursor-pointer transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                파일 추가
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.zip,.png,.jpg,.jpeg,.txt"
            />

            {attachedFiles.length === 0 ? (
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 hover:border-indigo-400 rounded-2xl p-4 text-center cursor-pointer bg-white transition-colors"
              >
                <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1.5" />
                <p className="text-xs font-bold text-slate-700">인수인계서, 산출물 문서, 체크리스트 파일을 첨부해 주세요.</p>
                <p className="text-[10px] text-slate-400 mt-0.5">PDF, Word, Excel, HWP, ZIP 파일 지원 (파일당 최대 5MB, 선택사항)</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attachedFiles.map(file => (
                  <div key={file.id} className="p-2.5 bg-white border border-slate-200 rounded-xl flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <File className="w-4 h-4 text-indigo-600 shrink-0" />
                      <span className="font-bold text-slate-900 truncate max-w-[280px]">{file.name}</span>
                      <span className="text-[10px] text-slate-400 shrink-0 font-mono">({formatFileSize(file.size)})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(file.id)}
                      className="p-1 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 cursor-pointer"
                      title="파일 삭제"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 3: Compliance & NDA Agreements */}
          <div className="bg-amber-50/50 border border-amber-200 rounded-2xl p-4 space-y-2.5">
            <span className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-amber-600" />
              3. 필수 확약 및 보안 서약 확인 (전체 필수 동의)
            </span>

            <label className="flex items-start gap-2.5 cursor-pointer p-1">
              <input
                type="checkbox"
                checked={confirmHandover}
                onChange={e => setConfirmHandover(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
              />
              <span className="text-[11px] text-slate-700 leading-snug">
                [인수인계 확약] 퇴사일까지 후임자 및 부서장에게 업무 산출물 및 계정 인계 완료에 동의합니다.
              </span>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer p-1">
              <input
                type="checkbox"
                checked={confirmAssetReturn}
                onChange={e => setConfirmAssetReturn(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
              />
              <span className="text-[11px] text-slate-700 leading-snug">
                [비품·장비 반납] 사원증, 노트북 PC, 법인카드 및 사내 인프라 접근 권한의 즉시 반납에 동의합니다.
              </span>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer p-1">
              <input
                type="checkbox"
                checked={confirmSecurityNda}
                onChange={e => setConfirmSecurityNda(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
              />
              <span className="text-[11px] text-slate-700 leading-snug">
                [보안 서약] 재직 중 지득한 회사의 영업비밀, 고객 데이터, 소스코드의 일체 누설 금지에 확약합니다.
              </span>
            </label>
          </div>

          {/* Section 4: Signature / Stamp Pad */}
          <div className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <PenTool className="w-4 h-4 text-indigo-600" />
                4. 신청인 정식 전자서명 / 인감 날인 *
              </span>
              <button
                type="button"
                onClick={() => setIsSignaturePadOpen(true)}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-xs cursor-pointer"
              >
                <Stamp className="w-3.5 h-3.5" />
                {savedSignature ? '서명 / 도장 다시 날인하기' : '전자서명 / 도장 날인하기'}
              </button>
            </div>

            {savedSignature ? (
              <div className="p-3 bg-white border-2 border-indigo-500/30 rounded-2xl flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={savedSignature.dataUrl}
                    alt="Signature"
                    className="h-12 max-w-[140px] object-contain border-b border-indigo-200"
                  />
                  <div>
                    <span className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      서명 날인 완료
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {savedSignature.type === 'DRAW' ? '직접 자필 서명' : '전자 인감 도장'} • {new Date().toLocaleTimeString()}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSignaturePadOpen(true)}
                  className="text-xs text-indigo-600 font-bold hover:underline"
                >
                  수정
                </button>
              </div>
            ) : (
              <div
                onClick={() => setIsSignaturePadOpen(true)}
                className="border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-2xl p-4 text-center cursor-pointer bg-white transition-colors"
              >
                <PenTool className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                <p className="text-xs font-bold text-slate-700">이곳을 클릭하여 자필 서명 또는 전자 인감을 날인해 주세요.</p>
                <p className="text-[10px] text-slate-400">서명 미날인 시 사직원 정식 제출이 불가합니다.</p>
              </div>
            )}
          </div>

          {/* Footer Submit */}
          <div className="pt-2 flex items-center justify-between border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-100 cursor-pointer"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-xs shadow-md flex items-center gap-2 cursor-pointer transition-all"
            >
              {isSubmitting ? (
                <>
                  <RotateCcw className="w-4 h-4 animate-spin" />
                  사직원 등록 및 결재선 상신 중...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  사직원 정식 제출 및 결재 요청 (상신)
                </>
              )}
            </button>
          </div>

        </form>

        {/* Signature Pad Modal */}
        {isSignaturePadOpen && (
          <SignaturePadModal
            isOpen={true}
            onClose={() => setIsSignaturePadOpen(false)}
            onConfirm={handleSignatureConfirmed}
            signerName={currentUser?.Name || '신청인'}
          />
        )}

      </div>
    </div>
  );
};

export default ResignationSubmitModal;
